import React, { useEffect, useState } from "react";
import { Checkbox, ImageList, ImageListItem } from "@mui/material";
import { IProImage } from "../../models/master";
import { useDispatch } from "react-redux";
import { PopoutNotification } from "../../AppState/popoutNotification";
import NOTIFICATION_TYPE from "../../AppState/NotificationType";
import {
  APP,
  DAMAGE_OSD_TYPE,
  EOsdType,
} from "../../constants";
import { getProImages } from "../../API/getProImages";
import Loader from "../loader/loader";
import { useApiHeaders } from "../../@hooks/useApiHeaders";
type Props = {
  osdNumber?: number;
  setSelectedImages: (imageName: string[]) => void;
  selectedImages: string[];
  handleImageSelect: (imageName: string) => void;
  isSendAPBScreen?: boolean;
  exceptionType?: string;
  isDisposition?: boolean;
};

const getSectionName = (
  isDisposition: boolean,
  isSendAPBScreen: boolean,
): string => {
  if (isDisposition) return "DispositionMail";
  if (isSendAPBScreen) return "APBMail";
  return "Investigation";
};

const SendAPBImageList: React.FC<Props> = ({
  osdNumber,
  setSelectedImages,
  selectedImages,
  handleImageSelect,
  exceptionType = "",
  isSendAPBScreen = true,
  isDisposition = false,
}) => {
  const dispatch = useDispatch();
  const [images, setImages] = useState<IProImage[]>([]);
  const [imageLoading, setImageLoading] = useState<boolean>(true);

  const sectionName = getSectionName(isDisposition, isSendAPBScreen);

  const apiHeaders = useApiHeaders({ sectionName, osdType: exceptionType });

  useEffect(() => {
    if (!osdNumber || !exceptionType) return;

    const getDocumentFilter = (exceptionType: string): string[] => {
      const exceptionTypeTrimmed = exceptionType.trim();
      const documentTypeMap: Record<string, string[]> = {
        PR: APP.sendDispositionImageType,
        RD: APP.sendDispositionImageType,
        RF: APP.sendDispositionImageType,
        DD: APP.sendDispositionImageType,
        DS: APP.sendAPBImageType,
        AS: APP.sendAPBImageType,
      };
      return documentTypeMap[exceptionTypeTrimmed] ?? APP.sendAPBImageType;
    };

    const handleImageResponse = (data: IProImage[]) => {
      const sortedImages = data
        .slice()
        .sort((a: IProImage, b: IProImage) =>
          a?.imageFilename?.localeCompare(b?.imageFilename),
        );
      setImages(sortedImages);
      setSelectedImages(data.map((image) => image.imageFilename ?? ""));

      if (data.length === 0 && isSendAPBScreen) {
        dispatch(
          PopoutNotification(
            NOTIFICATION_TYPE.INFO,
            "No image available",
            Date.now().toString(),
          ),
        );
      }
    };

    const fetchImages = async () => {
      setImageLoading(true);
      try {
        const documentFilter = getDocumentFilter(exceptionType);
        const { data, errorDetails } = await getProImages(
          {
            osdNumber,
            documentFilter,
          },
          apiHeaders,
        );

        if (data?.length === 0 && errorDetails?.length > 0) {
          dispatch(
            PopoutNotification(
              NOTIFICATION_TYPE.ERROR,
              errorDetails[0].error ??
                "Something went wrong. Please try again later.",
              Date.now().toString(),
            ),
          );
          return;
        }

        handleImageResponse(data);
      } catch (error) {
        if (isSendAPBScreen) {
          dispatch(
            PopoutNotification(
              NOTIFICATION_TYPE.ERROR,
              error as string,
              Date.now().toString(),
            ),
          );
        }
      } finally {
        setImageLoading(false);
      }
    };

    fetchImages();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [osdNumber, exceptionType]);

  const containerClass = DAMAGE_OSD_TYPE.includes(exceptionType as EOsdType)
    ? "padding-15 pt-0"
    : "content-area osd-description";

  return (
    <div className={containerClass} data-testid="content-area1">
      <ImageList
        cols={8}
        rowHeight={200}
        className="margin-grid"
        data-testid="image-list"
      >
        {images.map((image: IProImage) => (
          <ImageListItem
            key={image.imageFilename}
            className="grid-image"
            data-testid={`image-item-${image.imageFilename}`}
          >
            <img
              src={image.imageFilename}
              alt={image.typeDescription}
              data-testid={`image-${image.imageFilename}`}
            />
            <Checkbox
              data-testid={`image-checkbox-${image.imageFilename}`}
              checked={selectedImages.includes(image.imageFilename)}
              onChange={() => {
                handleImageSelect?.(image.imageFilename);
              }}
              className="img-checkbox"
              style={{ transform: "scale(1.5)" }}
              color="success"
              sx={{
                color: "#fff",
                "&.Mui-checked": {
                  color: "#3db3c5",
                },
              }}
              disableRipple
            />
            <div
              className="image-legend"
              data-testid={`legend-${image.imageFilename}`}
            >
              {image.docType}
            </div>
          </ImageListItem>
        ))}
      </ImageList>

      {images.length === 0 && !imageLoading && (
        <div className="no-image">
          No images found
          {isSendAPBScreen &&
          !DAMAGE_OSD_TYPE.includes(exceptionType as EOsdType)
            ? ", click on Send button to send email"
            : ""}
        </div>
      )}
      <Loader pageLoader={imageLoading} data-testid="loader" />
    </div>
  );
};

export default SendAPBImageList;
